import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Paper, Button, IconButton, Box, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../utils/IconHandler';
import TableRefreshButton from '../TableRefreshButton/TableRefreshButton';
import { getProducts, updateStockAndPrice } from '../../services/ProductService';
import { permissionOptions } from '../../utils/Enums';
import IOSSwitch from '../Switch/IOSSwitch';
import AddEditProductModal from './AddEditProductModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import ViewProductModal from './ViewProductModal';
import EditStockPriceModal from './EditStockPriceModal';
import { formatMoney } from '../../utils/currency';

function ProductsTable({ locationId, isBranch }) {
  const { t } = useTranslation();

  const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];
  const hasPermission = (permissionId) => userPermissions.includes(permissionId);

  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalState, setModalState] = useState({
    add: false,
    edit: false,
    editStock: false,
    view: false,
    delete: false
  });
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [totalRows, setTotalRows] = useState(0);

  // Ref para manejar debounce
  const searchTimeoutRef = useRef(null);

  const fetchProducts = useCallback(async (page, pageSize, search, currentSortModel = sortModel, currentFilterModel = filterModel) => {
    try {
      setLoading(true);
      const cleanSearch = search || '';
      const activeSort = Array.isArray(currentSortModel) ? currentSortModel[0] : null;
      const activeFilter = currentFilterModel?.items?.find((item) => item?.field && item?.operator);
      const data = await getProducts(locationId, page, pageSize, cleanSearch, {
        sortField: activeSort?.field,
        sortDirection: activeSort?.sort,
        filterField: activeFilter?.field,
        filterOperator: activeFilter?.operator,
        filterValue: activeFilter?.value,
      });

      if (data && typeof data === 'object') {
        setProducts(data.products || data.data || []);
        setTotalRows(data.total || data.totalCount || 0);
      } else {
        setProducts(data || []);
        setTotalRows(data?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [locationId, sortModel, filterModel]);

  // Cargar datos iniciales
  useEffect(() => {
    fetchProducts(paginationModel.page + 1, paginationModel.pageSize, searchTerm);
    
    // Cleanup timeout al desmontar
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [fetchProducts, paginationModel.page, paginationModel.pageSize, sortModel, filterModel]);

  // Funciones para manejar cambios de paginación
  const handlePaginationChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterModelChange = (newFilterModel) => {
    const normalizedItems = (newFilterModel?.items || [])
      .filter((item) => item?.field && item?.operator)
      .slice(0, 1);
    setFilterModel({ ...newFilterModel, items: normalizedItems });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Función para manejar cambios de búsqueda con debounce
  const handleSearchChange = (searchTerm) => {
    setSearchTerm(searchTerm);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce la búsqueda
    searchTimeoutRef.current = setTimeout(() => {
      fetchProducts(1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    }, 500);
  };

  const handleModal = (type, open, product = null) => {
    setModalState({ ...modalState, [type]: open });
    setSelectedProduct(product);
  };

  const handleAddProduct = async (newProduct) => {
    // Refetch data after adding
    fetchProducts(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    handleModal('add', false);
  };

  const handleEditProduct = async (updatedProduct) => {
    // Refetch data after editing
    fetchProducts(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    handleModal('edit', false);
  };

  const handleEditStockProduct = async (updatedProduct) => {
    // Refetch data after editing stock
    fetchProducts(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    handleModal('editStock', false);
  };

  const handleToggleUndefinedVisibility = useCallback(async (product, checked) => {
    try {
      await updateStockAndPrice(product.id, {
        location_id: parseInt(locationId, 10),
        price: product.price ?? 0,
        stock: product.stock ?? 0,
        minimum_stock: product.minimumStock ?? 0,
        suggested_stock: product.suggestedStock ?? 0,
        visible_in_location: checked,
      });

      setProducts((prevProducts) =>
        prevProducts.map((item) =>
          item.id === product.id ? { ...item, visibleInLocation: checked } : item
        )
      );
    } catch (error) {
      console.error('Error toggling undefined product visibility:', error);
    }
  }, [locationId]);

  const handleDeleteProduct = async () => {
    // Refetch data after deleting
    fetchProducts(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    handleModal('delete', false);
  };

  // El filtrado ahora se hace en el servidor, no necesitamos filteredProducts

  const canViewCost = hasPermission(permissionOptions.find(p => p.label === 'VIEW_COST_PRODUCTS').id);
  const canUpdateProductConfig = hasPermission(permissionOptions.find(p => p.label === 'UPDATE_PRODUCTS').id);
  const canUpdateProductStock = canUpdateProductConfig || hasPermission(permissionOptions.find(p => p.label === 'UPDATE_INVENTORY').id);
  const canDeleteProducts = hasPermission(permissionOptions.find(p => p.label === 'DELETE_PRODUCTS').id);
  const canViewProducts = hasPermission(permissionOptions.find(p => p.label === 'VIEW_PRODUCTS').id);
  const canViewStockHistory = hasPermission(permissionOptions.find(p => p.label === 'VIEW_STOCK_HISTORY').id);
  const canManageLocationVisibility = hasPermission(permissionOptions.find(p => p.label === 'VIEW_BUSINESS_UNITS').id);

  const showActionsColumn =
      canViewProducts || canUpdateProductConfig || canUpdateProductStock || canDeleteProducts;

  const columns = useMemo(() => {
  const baseColumns = [
    { field: 'name', headerName: t('name'), flex: 1.7, minWidth: 220 },
    ...(canViewCost
      ? [{
          field: 'cost',
          headerName: t('cost'),
          flex: 0.8,
          minWidth: 110,
          align: 'center',
          headerAlign: 'center',
          valueFormatter: (value, row) =>
            value !== null && value !== undefined
              ? formatMoney(value, row?.costCurrency)
              : 'N/A'
        }]
      : []),
    {
      field: 'price',
      headerName: t('price'),
      flex: 0.8,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value, row) =>
        value !== null && value !== undefined
          ? formatMoney(value, row?.priceCurrency)
          : 'N/A'
    },
    {
      field: 'stock',
      headerName: t('stock'),
      flex: 0.7,
      minWidth: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params.row;
        // DEFINED y RETAIL muestran stock, UNDEFINED no
        return (row.productType === 'DEFINED' || row.productType === 'RETAIL') ? row.stock.toLocaleString('de-DE') : 'N/A';
      }
    },
    {
      field: 'minimumStock',
      headerName: t('minimum_stock'),
      flex: 0.85,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params.row;
        return (row.productType === 'DEFINED' || row.productType === 'RETAIL') ? row.minimumStock.toLocaleString('de-DE') : 'N/A';
      }
    },
    {
      field: 'suggestedStock',
      headerName: t('suggested_stock'),
      flex: 0.85,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params.row;
        return (row.productType === 'DEFINED' || row.productType === 'RETAIL') ? row.suggestedStock.toLocaleString('de-DE') : 'N/A';
      }
    },
    ...(canManageLocationVisibility
      ? [{
          field: 'visibleInLocation',
          headerName: t('visible_in_location') || 'Visible',
          flex: 0.7,
          minWidth: 110,
          sortable: false,
          cellClassName: 'visible-switch-cell',
          align: 'center',
          headerAlign: 'center',
          renderCell: (params) => {
            const row = params.row;
            return (
              <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <IOSSwitch
                  checked={!!row.visibleInLocation}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => handleToggleUndefinedVisibility(row, event.target.checked)}
                />
              </Box>
            );
          }
        }]
      : []),
    { 
      field: 'productType', 
      headerName: t('product_type') || 'Tipo de producto', 
      flex: 1.1,
      minWidth: 150,
      align: 'center', 
      headerAlign: 'center',
      valueFormatter: (value) => {
        if (!value) return '';
        const type = value || 'DEFINED';
        if (type === 'DEFINED') return t('product_type_defined') || 'Definido';
        if (type === 'UNDEFINED') return t('product_type_undefined') || 'Indefinido';
        if (type === 'RETAIL') return t('product_type_retail') || 'Retail';
        return type;
      }
    }
  ];

  const actionButtonSx = {
    borderRadius: '50%',
    width: 36,
    height: 36,
    padding: 0,
  };

  if (showActionsColumn) {
    baseColumns.push({
      field: 'actions',
      headerName: t('actions'),
      flex: 1,
      minWidth: 200,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, width: '100%', height: '100%', minHeight: 52 }}>
          {canViewProducts && (
            !params.row.recipeDetailsHidden && (
              <IconButton aria-label={`Ver ${params.row.name}`} size="small" sx={actionButtonSx} onClick={() => handleModal('view', true, params.row)}>
                {getIcon('eye')}
              </IconButton>
            )
          )}
          {canUpdateProductConfig && (
            <IconButton aria-label={`Editar ${params.row.name}`} size="small" sx={actionButtonSx} onClick={() => handleModal('edit', true, params.row)}>
              {getIcon('settings')}
            </IconButton>
          )}
          {canUpdateProductStock && (
            <IconButton aria-label={`Editar stock de ${params.row.name}`} size="small" sx={actionButtonSx} onClick={() => handleModal('editStock', true, params.row)}>
              {getIcon('bagHandle')}
            </IconButton>
          )}
          {canDeleteProducts && (
            <IconButton aria-label={`Eliminar ${params.row.name}`} size="small" sx={actionButtonSx} onClick={() => handleModal('delete', true, params.row)}>
              {getIcon('trash')}
            </IconButton>
          )}
        </Box>
      )
    });
  }

  return baseColumns;
}, [t, canViewCost, canManageLocationVisibility, canViewProducts, canUpdateProductConfig, canUpdateProductStock, canDeleteProducts, handleToggleUndefinedVisibility]);

  return (
    <>
      <Paper sx={{ padding: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <TextField
            label={t('search')}
            placeholder={t('search_by_product_name')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{ width: '300px' }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TableRefreshButton
              loading={loading}
              onClick={() => fetchProducts(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel)}
            />
            {hasPermission(permissionOptions.find(p => p.label === 'CREATE_PRODUCTS').id) && (
              <Button variant="outlined" startIcon={getIcon('add')} onClick={() => handleModal('add', true)}>
                {t('add_product')}
              </Button>
            )}
          </Box>
        </Box>
        
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 920 }}>
            <DataGrid
              loading={loading}
              rows={products}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              sortingMode="server"
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
              filterMode="server"
              pageSizeOptions={[10, 25, 50]}
              paginationMode="server"
              rowCount={totalRows}
              autoHeight
              getRowClassName={(params) => {
                const { stock, minimumStock, suggestedStock, productType } = params.row;
                
                // Solo aplicar coloreo si el producto es DEFINED o RETAIL (tienen stock)
                if (productType !== 'DEFINED' && productType !== 'RETAIL') {
                  return '';
                }
                
                if (stock < minimumStock) {
                  return 'row-stock-red';
                }
                
                if (stock < suggestedStock) {
                  return 'row-stock-yellow';
                }
                
                return '';
              }}
              sx={{
                '& .row-stock-red': { backgroundColor: '#ffebee !important' },
                '& .row-stock-yellow': { backgroundColor: '#fff9e6 !important' },
                '& .MuiDataGrid-row:hover': { opacity: 0.9 },
                '& .MuiDataGrid-cell': { px: 1.25 },
                '& .MuiDataGrid-columnHeader': { px: 1.25 },
                '& .visible-switch-cell': {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                },
              }}
            />
          </Box>
        </Box>
        
      </Paper>
      
      {/* Modales */}
      {modalState.add && <AddEditProductModal locationId={locationId} open={modalState.add} onClose={() => handleModal('add', false)} onSave={handleAddProduct} />}
      {modalState.edit && <AddEditProductModal locationId={locationId} open={modalState.edit} product={selectedProduct} onClose={() => handleModal('edit', false)} onSave={handleEditProduct} />}
      {modalState.editStock && <EditStockPriceModal locationId={locationId} product={selectedProduct} open={modalState.editStock} onClose={() => handleModal('editStock', false)} onSave={handleEditStockProduct} canEditPrice={canUpdateProductConfig} canEditCost={canUpdateProductConfig} />}
      {modalState.view && <ViewProductModal locationId={locationId} open={modalState.view} product={selectedProduct} onClose={() => handleModal('view', false)} canViewStockHistory={canViewStockHistory} canViewCost={canViewCost} />}
      {modalState.delete && <DeleteConfirmModal open={modalState.delete} onClose={() => handleModal('delete', false)} onDelete={handleDeleteProduct} product={selectedProduct} />}
    </>
  );
}

export default ProductsTable;
